import React, { useEffect, useMemo, useState } from "react"
import ReactDOM from "react-dom"
import { Button, Table, Row, Col, Spinner, CardBody, Card } from "reactstrap"
import Skeleton from "react-loading-skeleton"
import axios from "axios"
import moment from "moment"
import "./customBackdropBlur.css"
import TableContainer from "components/Common/DataTableContainer"
import { toast } from "react-toastify"

const AssignDriver = ({ bookingData, onClose }) => {
  const [drivers, setDrivers] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDriver, setSelectedDriver] = useState(null)
  const [assigning, setAssigning] = useState(false)

  useEffect(() => {
    const fetchDrivers = async () => {
      setLoading(true)
      try {
        const token = localStorage.getItem("authToken")
        const bookingId = bookingData?.booking?.id
        const response = await axios.get(
          `${process.env.REACT_APP_WEB_APP_URL}/api/web/booking-requests/${bookingId}/nearby-drivers`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        if (response.status === 200) {
          const apiDrivers = response?.data?.data?.drivers
          setDrivers(apiDrivers)
        }
      } catch (error) {
        setDrivers([])
      } finally {
        setLoading(false)
      }
    }
    fetchDrivers()
  }, [bookingData])

  const columns = useMemo(
    () => [
      {
        header: "",
        accessorKey: "checkbox",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const isChecked = selectedDriver === row.original.id
          return (
            <span
              style={{
                position: "relative",
                display: "inline-block",
                width: 24,
                height: 24,
              }}
            > 
              <input
                type="checkbox"
                checked={isChecked}
                disabled={row.original.status === "in-booking"}
                onChange={() => setSelectedDriver(row.original.id)}
                style={{
                  width: 24,
                  height: 24,
                  accentColor: isChecked ? "#44d97b" : undefined,
                  cursor: "pointer",
                }}
              />
              {isChecked && (
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    left: 2,
                    pointerEvents: "none",
                    color: "#44d97b",
                    fontSize: 18,
                    fontWeight: "bold",
                  }}
                >
                  &#10003;
                </span>
              )}
            </span>
          )
        },
      },
      {
        header: "Driver",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Vehicle",
        accessorKey: "vehicle",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Distance",
        accessorKey: "distance",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const status = row.original.status?.toLowerCase()
          const free = status == "free"
          return (
            <span className={`badge ${free ? "bg-success" : "bg-danger"}`}>
              {status
                ? status.charAt(0).toUpperCase() + status.slice(1)
                : "busy"}
            </span>
          )
        },
      },
    ],
    [selectedDriver, drivers]
  )

  const totalRecords = drivers?.length ?? 0

  const transformedData = useMemo(() => {
    return drivers?.map(item => {
      return {
        ...item,
        name: item.name || "N/A",
        type: item?.vehicle?.type ?? "N/A",
        distance: item.distance || "N/A",
        vehicle: item?.vehicle?.make_name ?? "N/A",
        satus: item?.status ?? "N/A",
      }
    })
  }, [drivers])

  const handleAssign = async () => {
    if (!selectedDriver) return
    setAssigning(true)
    try {
      const token = localStorage.getItem("authToken")
      const response = await axios.post(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/booking-requests/${bookingData?.booking?.id}/assign-driver`,
        { driver_id: selectedDriver },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      if (response.status == 200) {
        toast.success("Booking assigned successfully")
        onClose()
      } else {
        toast.error("Failed to assign booking")
      }
    } catch (error) {
      console.error(error)
      toast.error(error.message || "Failed to assign booking")
    } finally {
      setAssigning(false)
    }
  }

  return ReactDOM.createPortal(
    <div className="backdrop">
      <div className="modal-box" style={{ overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center">
          <div></div>
          <h4 className="mb-3 text-center">Assign Driver</h4>
          <Button color="link" style={{ fontSize: 32 }} onClick={onClose}>
            &times;
          </Button>
        </div>
        {/* Request Info */}
        <div>
          <Card
            className=" shadow-none mt-2 border-2"
            style={{ fontSize: "0.9rem" }}
          >
            <CardBody>
              <Row className="mb-2">
                <Col sm="8">
                  <span className="text-muted">
                    {moment(bookingData?.booking?.created_at).format("DD/MM/YY h:mm A") ?? "N/A"}
                  </span>
                </Col>
                <Col sm="4" className="text-end">
                  <span
                    className={`px-3 py-1 rounded-1 text-white ${
                        bookingData?.booking?.status === "assigned" ? "bg-success" : "bg-warning"
                    }`}
                  >
                    {bookingData?.booking?.status ?? "N/A"}
                  </span>
                </Col>
              </Row>
              {/* <Row>
                <h4 className="mb-2">{bookingData?.booking?.id ?? "N/A"}</h4>
              </Row> */}
              <Row className="mb-2">
                {/* <Col xs="4">
                  <div>Category</div>
                  <div className="fw-semibold">Ride hailing</div>
                </Col> */}
                <Col xs="6">
                  <div>Type</div>
                  <div className="fw-semibold">
                    {bookingData?.booking?.type ?? "N/A"}
                  </div>
                </Col>
                <Col xs="6">
                  <div>Customer</div>
                  <div className="fw-semibold">
                    {bookingData?.customer?.name ?? "N/A"}
                  </div>
                </Col>
              </Row>
              <hr />
              <ol
                className="activity-checkout mb-0 px-2 mt-1"
                style={{ fontSize: "0.8rem" }}
              >
                <li className="checkout-item">
                  <div className="avatar-sm checkout-icon p-1">
                    <div className="avatar-title rounded-circle bg-primary">
                      <i
                        className="mdi mdi-map-marker text-white"
                        style={{ fontSize: "14px" }}
                      ></i>
                    </div>
                  </div>
                  <div className="feed-item-list">
                    <div>
                      <h6 className="mb-1" style={{ fontSize: "0.8rem" }}>
                        {loading ? (
                          <Skeleton width={130} />
                        ) : (
                          bookingData?.booking?.pickup_addr ?? "N/A"
                        )}
                      </h6>
                    </div>
                  </div>
                </li>

                <li className="checkout-item">
                  <div className="avatar-sm checkout-icon p-1">
                    <div className="avatar-title rounded-circle bg-primary">
                      <i
                        className="mdi mdi-map-marker-check text-white"
                        style={{ fontSize: "14px" }}
                      ></i>
                    </div>
                  </div>
                  <div className="feed-item-list">
                    <h6 className="mb-1" style={{ fontSize: "0.8rem" }}>
                      {loading ? (
                        <Skeleton width={130} />
                      ) : (
                        bookingData?.booking?.dest_addr ?? "N/A"
                      )}
                    </h6>
                  </div>
                </li>
              </ol>
            </CardBody>
          </Card>
        </div>
        {/* Nearby Drivers Table */}
        <div style={{ flex: 1 }}>
          {loading ? (
            <Skeleton count={10} height={40} />
          ) : (
            <TableContainer
              columns={columns}
              data={transformedData || []}
              isGlobalFilter={true}
              isPagination={true}
              SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
              pagination="pagination"
              paginationWrapper="dataTables_paginate paging_simple_numbers"
              tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
            />
          )}
        </div>
        {/* Assign Button */}
        <div className="mt-4 d-flex justify-content-center">
          <Button
            color="success"
            size="lg"
            style={{ width: "40%" }}
            disabled={!selectedDriver || assigning}
            onClick={handleAssign}
          >
            {assigning ? <Spinner size="sm" /> : "Assign"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default AssignDriver
